/**
 * Hook useOpenFromSearch
 * Permet d'ouvrir automatiquement le side panel d'une entité
 * lorsqu'on arrive sur une page de liste via la recherche globale
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseOpenFromSearchOptions<T> {
  /**
   * Liste des entités
   */
  data: T[];

  /**
   * Fonction pour récupérer l'ID d'une entité
   */
  getId: (item: T) => string;

  /**
   * Callback pour ouvrir le side panel avec l'entité
   */
  onOpen: (item: T) => void;

  /**
   * Loading state (optionnel)
   */
  loading?: boolean;

  /**
   * Skip - ne pas exécuter le hook (utile en mode server où data est limitée)
   */
  skip?: boolean;
}

/**
 * Hook pour ouvrir automatiquement le side panel depuis les query params
 * Utilisé quand on arrive sur une page depuis la recherche globale
 *
 * @example
 * ```tsx
 * useOpenFromSearch({
 *   data: associations,
 *   getId: (a) => a.id,
 *   onOpen: handleOpenSidePanel,
 *   loading,
 * });
 * ```
 */
export function useOpenFromSearch<T>({
  data,
  getId,
  onOpen,
  loading,
  skip = false,
}: UseOpenFromSearchOptions<T>) {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Ne rien faire si skip (ex: mode server avec données limitées)
    if (skip) return;

    // Attendre que les données soient chargées
    if (loading || !data || data.length === 0) return;

    // Lire le paramètre viewId
    const viewId = searchParams.get('viewId');
    if (!viewId) return;

    // Trouver l'entité correspondante
    const entity = data.find((item) => getId(item) === viewId);

    if (entity) {
      // Ouvrir le side panel
      onOpen(entity);

      // Retirer le paramètre de l'URL pour éviter de réouvrir
      // si l'utilisateur rafraîchit la page
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('viewId');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [data, loading, searchParams, setSearchParams, getId, onOpen, skip]);
}
